import { toast } from "sonner";
import { despachoFiscalInfoApi } from "@/modules/despacho/despachoFiscalInfoApi";
import { currentMonthRange } from "@/modules/despacho/despachoFiscalInfoHelpers";

const inFlightMonth = new Map();
const inFlightYear = new Map();

/**
 * Deriva YYYY-MM del mes a sincronizar al abrir Tareas fiscales.
 * Con rango de varios meses (p. ej. enero → mes actual) usa el mes en curso.
 */
export function resolveFiscalSyncPeriodKey({ period, dateFrom, dateTo, monthRange } = {}) {
  const currentMonth = String(monthRange?.from || currentMonthRange().from).slice(0, 7);
  if (period === "este_mes") return currentMonth;
  const from = String(dateFrom || "").slice(0, 7);
  const to = String(dateTo || "").slice(0, 7);
  if (from && to && from === to) return from;
  if (from && !to) return from;
  return currentMonth;
}

function notifySyncResult(sync, { force = false } = {}) {
  if (sync?.enabled === false) return;
  const inst = Number(sync?.instances_created || 0);
  const tasksN = Number(sync?.tasks_created || 0);
  if (inst > 0 || tasksN > 0) {
    toast.message(`Se generaron ${inst} vencimiento(s) y ${tasksN} tarea(s) del mes.`);
  } else if (force && inst === 0 && tasksN === 0) {
    toast.message(
      "No se generaron vencimientos ni tareas. Revise obligaciones activas en Clientes → Obligaciones y reglas en Calendario.",
    );
  }
}

/**
 * Sincroniza instancias y tareas fiscales del periodo (idempotente; deduplica llamadas concurrentes).
 * @param {{ monthKey?: string, notify?: boolean, force?: boolean }} opts
 * @returns {Promise<object|null>}
 */
export async function ensureFiscalCurrentMonthSynced({ monthKey, notify = true, force = false } = {}) {
  const key = monthKey || currentMonthRange().from.slice(0, 7);
  if (!force && inFlightMonth.has(key)) {
    return inFlightMonth.get(key);
  }

  const run = (async () => {
    try {
      const sync = await despachoFiscalInfoApi.autoSyncFiscalCurrentMonth({ periodKey: key });
      if (notify) notifySyncResult(sync, { force });
      return sync;
    } catch {
      return null;
    } finally {
      inFlightMonth.delete(key);
    }
  })();

  inFlightMonth.set(key, run);
  return run;
}

/**
 * Genera vencimientos y tareas de enero al mes actual del año (seguimiento anual).
 * @param {{ year?: number, notify?: boolean }} opts
 */
export async function ensureFiscalYearToDateSynced({ year, notify = false } = {}) {
  const y = Number(year) || new Date().getFullYear();
  if (inFlightYear.has(y)) {
    return inFlightYear.get(y);
  }

  const run = (async () => {
    try {
      const sync = await despachoFiscalInfoApi.autoSyncFiscalYearToDate({ year: y });
      if (notify) {
        const inst = Number(sync?.instances_created || 0);
        const tasksN = Number(sync?.tasks_created || 0);
        if (inst > 0 || tasksN > 0) {
          toast.message(
            `Se generaron ${inst} vencimiento(s) y ${tasksN} tarea(s) (${sync?.months_synced || 0} mes(es)).`,
          );
        }
      }
      return sync;
    } catch {
      return null;
    } finally {
      inFlightYear.delete(y);
    }
  })();

  inFlightYear.set(y, run);
  return run;
}
